/**
 * Detail bundle for a single `:ProtectiveFunction` (`$functionId` param).
 *
 * Returns:
 *   - `function`              — the node itself
 *   - `line` / `site`         — owning Line and Site (or the section's Site)
 *   - `solver`                — `[:SOLVED_BY]->(:LogicSolver)`
 *   - `section`               — `[:IN_SECTION]->(:SrsSection)` classification
 *   - `sensorRows`            — `SENSED_BY` edges with group/voting props
 *   - `finalElementRows`      — `ACTUATES` edges with group/action props
 *   - `protectedEquipment`    — `[:PROTECTS]->(:Equipment)` process assets
 *   - `implementedDiagrams`   — `[:IMPLEMENTED_BY]->(:FunctionLogicDiagram)`
 *   - `implementedPoints`     — `[:IMPLEMENTED_BY]->(:SafetyPoint)`
 *
 * Bounded by `LIMIT 1` (one function). Related lists are collected.
 */
export const PLANT_PROTECTIVE_FUNCTION_DETAIL_CYPHER = `
            MATCH (pf:ProtectiveFunction {id: $functionId})
            OPTIONAL MATCH (line:Line)-[:HAS_PROTECTIVE_FUNCTION]->(pf)
            OPTIONAL MATCH (site:Site)-[:HAS_LINE]->(line)
            OPTIONAL MATCH (pf)-[:SOLVED_BY]->(solver:LogicSolver)
            OPTIONAL MATCH (pf)-[:IN_SECTION]->(srsSection:SrsSection)
            OPTIONAL MATCH (srsSection)-[:PART_OF]->(sectionSite:Site)
            WITH pf, line, site, solver,
                 head(collect(distinct sectionSite)) AS sectionSiteCandidate,
                 head(collect(distinct srsSection)) AS section
            WITH pf, line, coalesce(site, sectionSiteCandidate) AS site, solver, section
            OPTIONAL MATCH (pf)-[sb:SENSED_BY]->(sensor:Equipment)
            WITH pf, line, site, solver, section,
                 collect(CASE WHEN sensor IS NULL THEN null ELSE {
                   equipment: sensor,
                   group: sb.group,
                   voting: sb.voting,
                   voting_type: sb.voting_type,
                   fail_direction: sb.fail_direction,
                   trip_setpoint: sb.trip_setpoint,
                   range: sb.range,
                   sensor_type: sb.sensor_type,
                   test_interval: sb.test_interval
                 } END) AS sensorRows
            OPTIONAL MATCH (pf)-[act:ACTUATES]->(fe:Equipment)
            WITH pf, line, site, solver, section, sensorRows,
                 collect(CASE WHEN fe IS NULL THEN null ELSE {
                   equipment: fe,
                   group: act.group,
                   voting: act.voting,
                   voting_type: act.voting_type,
                   action: act.action,
                   stroke: act.stroke,
                   fail_design: act.fail_design,
                   test_interval: act.test_interval
                 } END) AS finalElementRows
            OPTIONAL MATCH (pf)-[:PROTECTS]->(protected:Equipment)
            WITH pf, line, site, solver, section, sensorRows, finalElementRows,
                 collect(distinct protected) AS protectedEquipment
            OPTIONAL MATCH (pf)-[:IMPLEMENTED_BY]->(implFld:FunctionLogicDiagram)
            WITH pf, line, site, solver, section, sensorRows, finalElementRows,
                 protectedEquipment,
                 collect(distinct implFld) AS implementedDiagrams
            OPTIONAL MATCH (pf)-[:IMPLEMENTED_BY]->(implPoint:SafetyPoint)
            RETURN pf AS function,
                   line,
                   site,
                   solver,
                   section,
                   sensorRows,
                   finalElementRows,
                   protectedEquipment,
                   implementedDiagrams,
                   collect(distinct implPoint) AS implementedPoints
            LIMIT 1
          `
